import { z } from 'zod';

// src/i18n.ts
var LOCALES = ["en", "ru"];
var DEFAULT_LOCALE = "en";
var localizedTextSchema = z.object({
  en: z.string(),
  ru: z.string()
});
function isLocale(value) {
  return typeof value === "string" && LOCALES.includes(value);
}
function resolveLocale(input) {
  if (!input) return DEFAULT_LOCALE;
  const primary = input.trim().toLowerCase().split(/[-_]/)[0];
  return isLocale(primary) ? primary : DEFAULT_LOCALE;
}
function pickText(text2, locale) {
  if (text2 === void 0) return "";
  if (typeof text2 === "string") return text2;
  return text2[locale] || text2[DEFAULT_LOCALE] || "";
}
var text = (en, ru) => ({ en, ru });

// src/network.ts
var TON_NETWORKS = ["mainnet", "testnet"];
var TON_DNS_COLLECTION = "EQC3dNlesgVD8YbAazcauIrXBPfiVhMMr5YYk2in0Mtsz0Bz";
var NETWORK_DEFAULTS = {
  mainnet: {
    network: "mainnet",
    tonapiUrl: "https://tonapi.io",
    dnsCollection: TON_DNS_COLLECTION,
    explorerUrl: "https://tonviewer.com",
    domainMarketUrl: "https://dns.ton.org",
    chainId: "-239"
  },
  testnet: {
    network: "testnet",
    tonapiUrl: "https://testnet.tonapi.io",
    dnsCollection: TON_DNS_COLLECTION,
    explorerUrl: "https://testnet.tonviewer.com",
    domainMarketUrl: "https://dns.ton.org",
    chainId: "-3"
  }
};
function isTonNetwork(value) {
  return typeof value === "string" && TON_NETWORKS.includes(value);
}
function resolveNetworkConfig(network, overrides = {}) {
  const base = NETWORK_DEFAULTS[network];
  const merged = { ...base };
  for (const [key, value] of Object.entries(overrides)) {
    if (value === void 0 || value === null || value === "") continue;
    Object.assign(merged, { [key]: value });
  }
  return merged;
}

// src/dns.ts
var OP_CHANGE_DNS_RECORD = 1320284409;
var DNS_CATEGORY = {
  wallet: "wallet",
  site: "site",
  storage: "storage",
  nextResolver: "dns_next_resolver"
};
var DNS_TAG = {
  smcAddress: 40915,
  nextResolver: 47763,
  adnlAddress: 44289,
  storageAddress: 29811
};
var TON_DOMAIN_SUFFIX = ".ton";
var TON_DOMAIN_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,124}[a-z0-9])?\.ton$/;
function isTonDomain(value) {
  return TON_DOMAIN_PATTERN.test(value);
}
function domainLabel(domain) {
  if (!isTonDomain(domain)) return null;
  return domain.slice(0, -TON_DOMAIN_SUFFIX.length);
}
function tonGatewayHost(domain, gateway) {
  const label = domain.trim().toLowerCase().replace(/\.+$/, "").split(".").join("-d");
  return `${label}.${gateway.replace(/^https?:\/\//, "").replace(/\/+$/, "")}`;
}
function tonGatewayUrl(domain, gateway) {
  return `https://${tonGatewayHost(domain, gateway)}/`;
}
var BAG_ID_PATTERN = /^[0-9a-f]{64}$/;
function isBagId(value) {
  return BAG_ID_PATTERN.test(value);
}
function normalizeBagId(value) {
  return value.trim().toLowerCase().replace(/^0x/, "");
}

// src/limits.ts
var DEFAULT_LIMITS = {
  maxBundleBytes: 10 * 1024 * 1024,
  maxAssetBytes: 5 * 1024 * 1024,
  maxFilesPerSite: 100,
  maxHtmlChars: 5e5,
  maxCssChars: 3e5,
  maxJsChars: 3e5,
  keepVersionsPerSite: 5,
  publishRateLimitPerHour: 10,
  formRateLimitPerHour: 20
};
var ALLOWED_ASSET_MIME = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "image/svg+xml",
  "image/avif",
  "image/x-icon",
  "font/woff",
  "font/woff2",
  "application/pdf"
];
var MIME_EXTENSION = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/svg+xml": "svg",
  "image/avif": "avif",
  "image/x-icon": "ico",
  "font/woff": "woff",
  "font/woff2": "woff2",
  "application/pdf": "pdf"
};
function isAllowedAssetMime(value) {
  return ALLOWED_ASSET_MIME.includes(value);
}
var templateFieldTypeSchema = z.enum([
  "string",
  "text",
  "url",
  "email",
  "tel",
  "color",
  "number",
  "boolean",
  "image",
  "icon",
  "select",
  "address",
  "array"
]);
var templateFieldSchema = z.lazy(
  () => z.object({
    key: z.string().min(1).max(48).regex(/^[a-zA-Z][a-zA-Z0-9_]*$/, "field key must be a plain identifier"),
    label: localizedTextSchema,
    type: templateFieldTypeSchema,
    hint: localizedTextSchema.optional(),
    placeholder: localizedTextSchema.optional(),
    required: z.boolean().optional(),
    max: z.number().int().positive().optional(),
    min: z.number().int().optional(),
    options: z.array(z.object({ value: z.string().min(1).max(64), label: localizedTextSchema })).max(50).optional(),
    of: z.array(templateFieldSchema).max(20).optional(),
    maxItems: z.number().int().positive().max(100).optional(),
    default: z.unknown().optional()
  })
);
var templateManifestSchema = z.object({
  code: z.string().min(2).max(48).regex(/^[a-z][a-z0-9-]*$/, "template code must be kebab-case"),
  name: localizedTextSchema,
  description: localizedTextSchema,
  icon: z.string().min(1).max(48),
  version: z.number().int().positive(),
  hasContactForm: z.boolean().default(false),
  fields: z.array(templateFieldSchema).min(1).max(40)
});
function templateDataSchema(manifest) {
  const shape = {};
  for (const field of manifest.fields) {
    shape[field.key] = fieldSchema(field);
  }
  return z.object(shape).transform(stripUndefined);
}
function stripUndefined(value) {
  const out = {};
  for (const [key, entry] of Object.entries(value)) {
    if (entry !== void 0) out[key] = entry;
  }
  return out;
}
function fieldSchema(field) {
  const optionalize = (schema) => field.required ? schema : schema.optional();
  switch (field.type) {
    case "string":
    case "text":
      return optionalize(z.string().max(field.max ?? 500));
    case "url":
      return optionalize(z.string().max(field.max ?? 300));
    case "email":
      return optionalize(z.email().max(field.max ?? 200));
    case "tel":
      return optionalize(z.string().max(field.max ?? 32));
    case "color":
      return optionalize(z.string().regex(/^#[0-9a-fA-F]{6}$/, "expected #rrggbb"));
    case "number":
      return optionalize(z.number().finite());
    case "boolean":
      return optionalize(z.boolean());
    case "icon":
      return optionalize(z.string().max(48));
    case "address":
      return optionalize(z.string().min(48).max(70));
    case "image":
      return optionalize(z.string().max(200));
    case "select": {
      const values = (field.options ?? []).map((option) => option.value);
      if (values.length === 0) return optionalize(z.string().max(64));
      return optionalize(z.enum(values));
    }
    case "array": {
      const entryShape = {};
      for (const child of field.of ?? []) {
        entryShape[child.key] = fieldSchema(child);
      }
      const entry = z.object(entryShape);
      return optionalize(z.array(entry).max(field.maxItems ?? 50));
    }
    default:
      return optionalize(z.unknown());
  }
}
var SITE_KINDS = ["TEMPLATE", "CUSTOM"];
var VERSION_STATUSES = [
  "DRAFT",
  "BUILDING",
  "BUILT",
  "PUBLISHING",
  "LIVE",
  "FAILED",
  "SUPERSEDED"
];
var assetRefSchema = z.object({
  id: z.string().min(1).max(64),
  path: z.string().min(1).max(200),
  mime: z.string().min(1).max(100),
  size: z.number().int().nonnegative()
});
var customSourceSchema = z.object({
  kind: z.literal("CUSTOM"),
  html: z.string().max(DEFAULT_LIMITS.maxHtmlChars),
  css: z.string().max(DEFAULT_LIMITS.maxCssChars).default(""),
  js: z.string().max(DEFAULT_LIMITS.maxJsChars).default("")
});
var templateSourceSchema = z.object({
  kind: z.literal("TEMPLATE"),
  templateCode: z.string().min(2).max(48),
  templateVersion: z.number().int().positive(),
  data: z.record(z.string(), z.unknown())
});
var siteSourceSchema = z.discriminatedUnion("kind", [
  customSourceSchema,
  templateSourceSchema
]);
var siteSettingsSchema = z.object({
  title: z.string().max(120).default(""),
  description: z.string().max(300).default(""),
  faviconAssetId: z.string().max(64).optional(),
  language: z.string().max(8).regex(/^[a-z]{2}(-[A-Za-z0-9]{2,8})?$/).default("en"),
  contactFormEnabled: z.boolean().default(false)
});
var versionSourceSchema = z.object({
  source: siteSourceSchema,
  settings: siteSettingsSchema,
  assets: z.array(assetRefSchema).max(DEFAULT_LIMITS.maxFilesPerSite)
});
function slugFromDomain(domain) {
  return domain.toLowerCase().replace(/\.ton$/, "").replace(/[^a-z0-9-]/g, "-").replace(/-{2,}/g, "-").replace(/^-|-$/g, "").slice(0, 60);
}

// src/forms.ts
var FORMS_PATH = "/api/forms";
function formEndpointPath(siteId) {
  return `${FORMS_PATH}/f/${siteId}`;
}
var telegramAuthRequestSchema = z.object({
  initData: z.string().min(1).max(8192)
});
var authTokensSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number().int().positive()
});
var sessionUserSchema = z.object({
  id: z.string(),
  telegramId: z.string(),
  firstName: z.string(),
  username: z.string().nullable(),
  languageCode: z.string().nullable(),
  locale: z.enum(LOCALES).nullable()
});
var tonProofPayloadSchema = z.object({
  payload: z.string(),
  validUntil: z.number().int().positive()
});
var tonProofVerifyRequestSchema = z.object({
  address: z.string().min(1).max(80),
  network: z.enum(TON_NETWORKS),
  publicKey: z.string().min(1).max(200),
  walletStateInit: z.string().min(1),
  proof: z.object({
    timestamp: z.number().int().positive(),
    domain: z.object({
      lengthBytes: z.number().int().positive(),
      value: z.string().min(1).max(253)
    }),
    signature: z.string().min(1),
    payload: z.string().min(1)
  })
});
var walletSchema = z.object({
  id: z.string(),
  address: z.string(),
  friendlyAddress: z.string(),
  network: z.enum(TON_NETWORKS),
  verifiedAt: z.string()
});
var domainSchema = z.object({
  name: z.string(),
  nftAddress: z.string(),
  currentBagId: z.string().nullable(),
  managedByUs: z.boolean(),
  siteId: z.string().nullable(),
  expiresAt: z.string().nullable()
});
var domainListSchema = z.object({
  network: z.enum(TON_NETWORKS),
  walletAddress: z.string(),
  domains: z.array(domainSchema),
  empty: z.boolean(),
  marketUrl: z.string()
});
var createSiteRequestSchema = z.object({
  domain: z.string().min(5).max(130),
  kind: z.enum(SITE_KINDS),
  templateCode: z.string().min(2).max(48).optional()
});
var changeTemplateRequestSchema = z.object({
  kind: z.enum(SITE_KINDS),
  templateCode: z.string().min(2).max(48).optional()
});
var saveDraftRequestSchema = z.object({
  source: siteSourceSchema,
  settings: siteSettingsSchema,
  assetIds: z.array(z.string().max(64)).max(200).default([])
});
var siteVersionSchema = z.object({
  id: z.string(),
  number: z.number().int().positive(),
  status: z.enum(VERSION_STATUSES),
  bagId: z.string().nullable(),
  txHash: z.string().nullable(),
  error: z.string().nullable(),
  buildHash: z.string().nullable(),
  sizeBytes: z.number().int().nonnegative().nullable(),
  createdAt: z.string(),
  publishedAt: z.string().nullable()
});
var siteSchema = z.object({
  id: z.string(),
  domain: z.string(),
  slug: z.string(),
  kind: z.enum(SITE_KINDS),
  templateCode: z.string().nullable(),
  network: z.enum(TON_NETWORKS),
  liveVersion: siteVersionSchema.nullable(),
  draftVersion: siteVersionSchema.nullable(),
  hasUnpublishedChanges: z.boolean(),
  needsSiteRecord: z.boolean(),
  isCovered: z.boolean(),
  coveredUntil: z.string().nullable(),
  suspendedAt: z.string().nullable(),
  createdAt: z.string()
});
var preparedTransactionSchema = z.object({
  validUntil: z.number().int().positive(),
  network: z.string(),
  from: z.string().optional(),
  messages: z.array(
    z.object({
      address: z.string(),
      amount: z.string(),
      payload: z.string()
    })
  ).min(1)
});
var publishPlanSchema = z.object({
  versionId: z.string(),
  bagId: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  fileCount: z.number().int().nonnegative(),
  transaction: preparedTransactionSchema,
  feeEstimateTon: z.string()
});
var confirmPublishRequestSchema = z.object({
  boc: z.string().min(1).max(2e4)
});
var assetUploadResponseSchema = z.object({
  id: z.string(),
  path: z.string(),
  mime: z.string(),
  size: z.number().int().nonnegative(),
  previewUrl: z.string()
});
var formSubmissionSchema = z.object({
  fields: z.record(z.string().max(64), z.string().max(4e3))
});
var apiErrorSchema = z.object({
  statusCode: z.number().int(),
  code: z.string(),
  message: z.string(),
  details: z.unknown().optional()
});
var API_ERROR_CODE = {
  invalidInitData: "INVALID_INIT_DATA",
  invalidTonProof: "INVALID_TON_PROOF",
  walletNotVerified: "WALLET_NOT_VERIFIED",
  domainNotOwned: "DOMAIN_NOT_OWNED",
  noDomains: "NO_DOMAINS",
  bundleTooLarge: "BUNDLE_TOO_LARGE",
  buildFailed: "BUILD_FAILED",
  storageUnavailable: "STORAGE_UNAVAILABLE",
  rateLimited: "RATE_LIMITED",
  planRequired: "PLAN_REQUIRED",
  siteQuotaExceeded: "SITE_QUOTA_EXCEEDED",
  customCodeRequired: "CUSTOM_CODE_REQUIRED",
  orderNotPaid: "ORDER_NOT_PAID",
  billingDisabled: "BILLING_DISABLED"
};
var BILLING_SCOPES = ["SITE", "ACCOUNT"];
var BILLING_PERIODS = ["MONTH", "QUARTER", "HALF_YEAR", "YEAR", "LIFETIME"];
var ENTITLEMENT_SOURCES = ["PURCHASE", "TRIAL", "GRANT"];
var BILLING_FEATURES = ["PUBLISH", "CUSTOM_CODE"];
var ORDER_STATUSES = ["PENDING", "PAID", "EXPIRED", "CANCELLED"];
var PERIOD_MONTHS = {
  MONTH: 1,
  QUARTER: 3,
  HALF_YEAR: 6,
  YEAR: 12
};
function periodMonths(period) {
  return period === "LIFETIME" ? null : PERIOD_MONTHS[period];
}
function addPeriod(from, period) {
  const months = periodMonths(period);
  if (months === null) return null;
  const result = new Date(from.getTime());
  const targetMonth = result.getUTCMonth() + months;
  const day = result.getUTCDate();
  result.setUTCDate(1);
  result.setUTCMonth(targetMonth);
  const lastDay = new Date(
    Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0)
  ).getUTCDate();
  result.setUTCDate(Math.min(day, lastDay));
  return result;
}
function renewalEnd(currentEnd, period, now) {
  if (period === "LIFETIME") return null;
  const base = currentEnd && currentEnd.getTime() > now.getTime() ? currentEnd : now;
  return addPeriod(base, period);
}
var NANOGRAM_PER_GRAM = 1000000000n;
function formatGram(nanogram) {
  const negative = nanogram < 0n;
  const value = negative ? -nanogram : nanogram;
  const whole = value / NANOGRAM_PER_GRAM;
  const fraction = (value % NANOGRAM_PER_GRAM).toString().padStart(9, "0").replace(/0+$/, "");
  return `${negative ? "-" : ""}${whole}${fraction ? `.${fraction}` : ""}`;
}
function parseGram(input) {
  const trimmed = input.trim().replace(",", ".");
  if (!/^\d+(\.\d{1,9})?$/.test(trimmed)) {
    throw new Error(`Expected an amount in GRAM with up to 9 decimals, got "${input}"`);
  }
  const [whole = "0", fraction = ""] = trimmed.split(".");
  return BigInt(whole) * NANOGRAM_PER_GRAM + BigInt(fraction.padEnd(9, "0"));
}
function isActive(entitlement, now) {
  if (entitlement.revokedAt !== null) return false;
  if (entitlement.startsAt.getTime() > now.getTime()) return false;
  if (entitlement.endsAt === null) return true;
  return entitlement.endsAt.getTime() > now.getTime();
}
function resolveCoverage(input) {
  const { entitlements, sites, now } = input;
  const active = entitlements.filter((entitlement) => isActive(entitlement, now));
  const forPublishing = active.filter((entitlement) => entitlement.feature === "PUBLISH");
  const siteCoveredUntil = /* @__PURE__ */ new Map();
  for (const entitlement of forPublishing) {
    if (entitlement.siteId === null) continue;
    const known = siteCoveredUntil.get(entitlement.siteId);
    if (siteCoveredUntil.has(entitlement.siteId) && known === null) continue;
    if (!siteCoveredUntil.has(entitlement.siteId) || entitlement.endsAt === null || known && entitlement.endsAt.getTime() > known.getTime()) {
      siteCoveredUntil.set(entitlement.siteId, entitlement.endsAt);
    }
  }
  const accountGrants = forPublishing.filter((entitlement) => entitlement.siteId === null);
  const accountQuota = accountGrants.reduce(
    (largest, entitlement) => Math.max(largest, entitlement.siteQuota),
    0
  );
  let accountCoveredUntil = null;
  let sawLifetime = false;
  for (const grant of accountGrants) {
    if (grant.endsAt === null) {
      sawLifetime = true;
      continue;
    }
    if (!accountCoveredUntil || grant.endsAt.getTime() > accountCoveredUntil.getTime()) {
      accountCoveredUntil = grant.endsAt;
    }
  }
  if (sawLifetime) accountCoveredUntil = null;
  const trial = accountGrants.find((entitlement) => entitlement.source === "TRIAL");
  const published = sites.filter((site) => site.publishedAt !== null).sort((a, b) => a.publishedAt.getTime() - b.publishedAt.getTime());
  const coveredSiteIds = /* @__PURE__ */ new Set();
  const uncoveredSiteIds = /* @__PURE__ */ new Set();
  let accountQuotaUsed = 0;
  for (const site of published) {
    if (siteCoveredUntil.has(site.id)) {
      coveredSiteIds.add(site.id);
      continue;
    }
    if (accountQuotaUsed < accountQuota) {
      accountQuotaUsed += 1;
      coveredSiteIds.add(site.id);
      continue;
    }
    uncoveredSiteIds.add(site.id);
  }
  return {
    accountQuota,
    coveredSiteIds,
    uncoveredSiteIds,
    accountQuotaUsed,
    trialEndsAt: trial?.endsAt ?? null,
    accountCoveredUntil,
    siteCoveredUntil,
    customCode: resolveCustomCode(active)
  };
}
function resolveCustomCode(active) {
  const grants = active.filter(
    (entitlement) => entitlement.feature === "CUSTOM_CODE" && entitlement.siteId === null
  );
  if (grants.length === 0) return { unlocked: false, until: null, source: null };
  const forever = grants.find((entitlement) => entitlement.endsAt === null);
  if (forever) return { unlocked: true, until: null, source: forever.source };
  const latest = grants.reduce(
    (best, entitlement) => entitlement.endsAt.getTime() > best.endsAt.getTime() ? entitlement : best
  );
  return { unlocked: true, until: latest.endsAt, source: latest.source };
}
function coversSite(coverage, siteId) {
  if (coverage.coveredSiteIds.has(siteId)) return true;
  if (coverage.siteCoveredUntil.has(siteId)) return true;
  return coverage.accountQuotaUsed < coverage.accountQuota;
}
function canPublish(input) {
  const coverage = resolveCoverage(input);
  if (coverage.coveredSiteIds.has(input.siteId) || coverage.siteCoveredUntil.has(input.siteId)) {
    return { allowed: true, reason: "covered" };
  }
  if (coverage.accountQuotaUsed < coverage.accountQuota) {
    return { allowed: true, reason: "freeSlot" };
  }
  return { allowed: false, reason: coverage.accountQuota === 0 ? "noPlan" : "quotaExceeded" };
}
var billingPlanSchema = z.object({
  code: z.string().min(1).max(40),
  scope: z.enum(BILLING_SCOPES),
  period: z.enum(BILLING_PERIODS),
  feature: z.enum(BILLING_FEATURES),
  siteQuota: z.number().int().nonnegative(),
  priceNanogram: z.string().regex(/^\d+$/),
  priceGram: z.string(),
  name: localizedTextSchema,
  isActive: z.boolean(),
  sortOrder: z.number().int()
});
var entitlementSchema = z.object({
  id: z.string(),
  siteId: z.string().nullable(),
  siteDomain: z.string().nullable(),
  planCode: z.string().nullable(),
  source: z.enum(ENTITLEMENT_SOURCES),
  feature: z.enum(BILLING_FEATURES),
  siteQuota: z.number().int(),
  startsAt: z.string(),
  endsAt: z.string().nullable(),
  note: z.string().nullable()
});
var billingStateSchema = z.object({
  enabled: z.boolean(),
  accountQuota: z.number().int(),
  accountQuotaUsed: z.number().int(),
  trialEndsAt: z.string().nullable(),
  trialDaysLeft: z.number().int().nullable(),
  accountCoveredUntil: z.string().nullable(),
  coveredSiteIds: z.array(z.string()),
  uncoveredSiteIds: z.array(z.string()),
  entitlements: z.array(entitlementSchema),
  customCode: z.object({
    unlocked: z.boolean(),
    until: z.string().nullable(),
    source: z.enum(ENTITLEMENT_SOURCES).nullable()
  })
});
var createOrderRequestSchema = z.object({
  planCode: z.string().min(1).max(40),
  siteId: z.string().min(1).max(40).optional()
});
var orderSchema = z.object({
  id: z.string(),
  planCode: z.string(),
  siteId: z.string().nullable(),
  status: z.enum(ORDER_STATUSES),
  amountNanogram: z.string(),
  amountGram: z.string(),
  payTo: z.string(),
  memo: z.string(),
  expiresAt: z.string(),
  paidAt: z.string().nullable()
});
var createOrderResponseSchema = z.object({
  order: orderSchema,
  transaction: z.object({
    validUntil: z.number().int().positive(),
    network: z.string(),
    from: z.string().optional(),
    messages: z.array(
      z.object({ address: z.string(), amount: z.string(), payload: z.string().optional() })
    )
  })
});
var ORDER_MEMO_PREFIX = "TB-";
var ORDER_MEMO_PATTERN = /^TB-[0-9A-HJ-NP-Z]{8}$/;
var MEMO_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
function isOrderMemo(value) {
  return ORDER_MEMO_PATTERN.test(value);
}
function memoFromComment(comment) {
  if (!comment) return null;
  const match = /TB-[0-9A-HJ-NP-Z]{8}/.exec(comment.toUpperCase());
  return match ? match[0] : null;
}
function paymentVerdict(order, transfer) {
  if (memoFromComment(transfer.comment) !== order.memo) return "wrongMemo";
  if (transfer.amountNanogram < order.amountNanogram) return "underpaid";
  if (transfer.timestampMs > order.expiresAt.getTime()) return "late";
  return "settle";
}

// src/billing-catalog.ts
var BILLING_CATALOG = [
  {
    code: "site-1m",
    scope: "SITE",
    period: "MONTH",
    siteQuota: 1,
    priceGram: "3",
    name: text("One site, 1 month", "\u041E\u0434\u0438\u043D \u0441\u0430\u0439\u0442, 1 \u043C\u0435\u0441\u044F\u0446"),
    sortOrder: 10
  },
  {
    code: "site-3m",
    scope: "SITE",
    period: "QUARTER",
    siteQuota: 1,
    priceGram: "8",
    name: text("One site, 3 months", "\u041E\u0434\u0438\u043D \u0441\u0430\u0439\u0442, 3 \u043C\u0435\u0441\u044F\u0446\u0430"),
    sortOrder: 20
  },
  {
    code: "site-6m",
    scope: "SITE",
    period: "HALF_YEAR",
    siteQuota: 1,
    priceGram: "15",
    name: text("One site, 6 months", "\u041E\u0434\u0438\u043D \u0441\u0430\u0439\u0442, 6 \u043C\u0435\u0441\u044F\u0446\u0435\u0432"),
    sortOrder: 30
  },
  {
    code: "site-1y",
    scope: "SITE",
    period: "YEAR",
    siteQuota: 1,
    priceGram: "25",
    name: text("One site, 1 year", "\u041E\u0434\u0438\u043D \u0441\u0430\u0439\u0442, 1 \u0433\u043E\u0434"),
    sortOrder: 40
  },
  {
    code: "site-forever",
    scope: "SITE",
    period: "LIFETIME",
    siteQuota: 1,
    priceGram: "60",
    name: text("One site, forever", "\u041E\u0434\u0438\u043D \u0441\u0430\u0439\u0442, \u043D\u0430\u0432\u0441\u0435\u0433\u0434\u0430"),
    sortOrder: 50
  },
  {
    code: "pro-1m",
    scope: "ACCOUNT",
    period: "MONTH",
    siteQuota: 5,
    priceGram: "10",
    name: text("Pro, 1 month", "Pro, 1 \u043C\u0435\u0441\u044F\u0446"),
    sortOrder: 60
  },
  {
    code: "pro-1y",
    scope: "ACCOUNT",
    period: "YEAR",
    siteQuota: 5,
    priceGram: "90",
    name: text("Pro, 1 year", "Pro, 1 \u0433\u043E\u0434"),
    sortOrder: 70
  },
  {
    code: "business-1m",
    scope: "ACCOUNT",
    period: "MONTH",
    siteQuota: 25,
    priceGram: "35",
    name: text("Business, 1 month", "Business, 1 \u043C\u0435\u0441\u044F\u0446"),
    sortOrder: 80
  },
  {
    code: "business-1y",
    scope: "ACCOUNT",
    period: "YEAR",
    siteQuota: 25,
    priceGram: "300",
    name: text("Business, 1 year", "Business, 1 \u0433\u043E\u0434"),
    sortOrder: 90
  },
  {
    code: "code-1m",
    scope: "ACCOUNT",
    period: "MONTH",
    feature: "CUSTOM_CODE",
    siteQuota: 0,
    priceGram: "5",
    name: text("Custom code, 1 month", "\u0421\u0432\u043E\u0439 \u043A\u043E\u0434, 1 \u043C\u0435\u0441\u044F\u0446"),
    sortOrder: 100
  },
  {
    code: "code-forever",
    scope: "ACCOUNT",
    period: "LIFETIME",
    feature: "CUSTOM_CODE",
    siteQuota: 0,
    priceGram: "40",
    name: text("Custom code, forever", "\u0421\u0432\u043E\u0439 \u043A\u043E\u0434, \u043D\u0430\u0432\u0441\u0435\u0433\u0434\u0430"),
    sortOrder: 110
  }
];
function catalogPriceNanogram(entry) {
  return BigInt(entry.priceGram) * NANOGRAM_PER_GRAM;
}
var TRIAL_SITE_QUOTA = 1;
var trialSettingsSchema = z.object({
  enabled: z.boolean(),
  days: z.number().int().min(0).max(365),
  planCode: z.string().max(40).nullable(),
  siteQuota: z.number().int().min(0).max(100),
  customCode: z.boolean()
});
var trialSettingsPatchSchema = trialSettingsSchema.partial();
var DEFAULT_TRIAL_SETTINGS = {
  enabled: true,
  days: 14,
  planCode: null,
  siteQuota: TRIAL_SITE_QUOTA,
  customCode: false
};
var appSettingsSchema = z.object({
  trial: trialSettingsSchema
});
var SETTING_KEYS = {
  trial: "trial"
};
function parseTrialSettings(value, fallback = DEFAULT_TRIAL_SETTINGS) {
  const parsed = trialSettingsSchema.safeParse(value);
  return parsed.success ? parsed.data : fallback;
}

// src/jobs.ts
var QUEUE = {
  build: "build",
  watch: "watch",
  cleanup: "cleanup",
  payments: "payments",
  expiry: "expiry",
  notify: "notify"
};
function jobId(...parts) {
  return parts.map((part) => String(part).replace(/[^A-Za-z0-9_-]/g, "-")).join("-");
}
var WATCH_TIMEOUT_MS = 20 * 60 * 1e3;
var WATCH_INTERVAL_MS = 10 * 1e3;
var VERIFY_TIMEOUT_MS = 3 * 60 * 1e3;
var PAYMENT_SWEEP_MS = 20 * 1e3;
var EXPIRY_SWEEP_MS = 60 * 60 * 1e3;
var NOTIFY_ATTEMPTS = 8;
var NOTIFY_BACKOFF_MS = 5e3;
var USER_SCOPED_NOTIFICATIONS = /* @__PURE__ */ new Set([
  "paymentReceived",
  "coverageLapsed",
  "coverageExpiring",
  "trialStarted",
  "trialExpiring",
  "trialEnded",
  "customCodeExpiring",
  "customCodeEnded",
  "accessGranted",
  "accessRevoked",
  "orderExpired"
]);
function isUserNotification(message) {
  return USER_SCOPED_NOTIFICATIONS.has(message.type);
}

// src/env.ts
function withoutEmptyEnv(source) {
  return Object.fromEntries(Object.entries(source).filter(([, value]) => value !== ""));
}

export { ALLOWED_ASSET_MIME, API_ERROR_CODE, BAG_ID_PATTERN, BILLING_CATALOG, BILLING_FEATURES, BILLING_PERIODS, BILLING_SCOPES, DEFAULT_LIMITS, DEFAULT_LOCALE, DEFAULT_TRIAL_SETTINGS, DNS_CATEGORY, DNS_TAG, ENTITLEMENT_SOURCES, EXPIRY_SWEEP_MS, FORMS_PATH, LOCALES, MEMO_ALPHABET, MIME_EXTENSION, NANOGRAM_PER_GRAM, NETWORK_DEFAULTS, NOTIFY_ATTEMPTS, NOTIFY_BACKOFF_MS, OP_CHANGE_DNS_RECORD, ORDER_MEMO_PATTERN, ORDER_MEMO_PREFIX, ORDER_STATUSES, PAYMENT_SWEEP_MS, QUEUE, SETTING_KEYS, SITE_KINDS, TON_DNS_COLLECTION, TON_DOMAIN_PATTERN, TON_DOMAIN_SUFFIX, TON_NETWORKS, TRIAL_SITE_QUOTA, USER_SCOPED_NOTIFICATIONS, VERIFY_TIMEOUT_MS, VERSION_STATUSES, WATCH_INTERVAL_MS, WATCH_TIMEOUT_MS, addPeriod, apiErrorSchema, appSettingsSchema, assetRefSchema, assetUploadResponseSchema, authTokensSchema, billingPlanSchema, billingStateSchema, canPublish, catalogPriceNanogram, changeTemplateRequestSchema, confirmPublishRequestSchema, coversSite, createOrderRequestSchema, createOrderResponseSchema, createSiteRequestSchema, customSourceSchema, domainLabel, domainListSchema, domainSchema, entitlementSchema, formEndpointPath, formSubmissionSchema, formatGram, isActive, isAllowedAssetMime, isBagId, isLocale, isOrderMemo, isTonDomain, isTonNetwork, isUserNotification, jobId, localizedTextSchema, memoFromComment, normalizeBagId, orderSchema, parseGram, parseTrialSettings, paymentVerdict, periodMonths, pickText, preparedTransactionSchema, publishPlanSchema, renewalEnd, resolveCoverage, resolveLocale, resolveNetworkConfig, saveDraftRequestSchema, sessionUserSchema, siteSchema, siteSettingsSchema, siteSourceSchema, siteVersionSchema, slugFromDomain, telegramAuthRequestSchema, templateDataSchema, templateFieldSchema, templateFieldTypeSchema, templateManifestSchema, templateSourceSchema, text, tonGatewayHost, tonGatewayUrl, tonProofPayloadSchema, tonProofVerifyRequestSchema, trialSettingsPatchSchema, trialSettingsSchema, versionSourceSchema, walletSchema, withoutEmptyEnv };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map